import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from './pb';
import { useActiveTenantId } from './tenantContext';
import { normalizePhone } from './phone';
import type {
  Lead,
  Pipeline,
  PipelineStage,
  Contact,
  Activity,
  Task,
  Attribution,
  User,
} from './types';

// ─────────────────────────────────────────────────────────────────────────
// Todas las queries filtran por el tenant ACTIVO (selectedTenantId del
// TenantContext). Las rules de PB ya filtran por @request.auth.tenant para
// client_admin/sales_rep, pero el superadmin tiene `tenant=null` y las rules
// le dejan ver todo — sin este filter explícito mezclaría datos entre tenants.
//
// Las queries se desactivan (enabled=false) hasta que haya un tenant activo,
// para evitar fetch sin filtro mientras carga la lista de tenants.
// ─────────────────────────────────────────────────────────────────────────

export function usePipelines() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['pipelines', tenantId],
    queryFn: async () => {
      const res = await pb.collection('pipelines').getList<Pipeline>(1, 50, {
        filter: `tenant = "${tenantId}" && archived = false`,
        sort: '-is_default,name',
      });
      return res.items;
    },
    enabled: !!tenantId,
  });
}

export function usePipelineStages(pipelineId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['pipeline_stages', tenantId, pipelineId],
    queryFn: async () => {
      if (!pipelineId) return [];
      const res = await pb.collection('pipeline_stages').getList<PipelineStage>(1, 50, {
        filter: `tenant = "${tenantId}" && pipeline = "${pipelineId}" && archived = false`,
        sort: 'position',
      });
      return res.items;
    },
    enabled: !!tenantId && !!pipelineId,
  });
}

export function useLeads(
  pipelineId: string | undefined,
  range?: { createdFrom?: string; createdTo?: string },
) {
  const tenantId = useActiveTenantId();
  const createdFrom = range?.createdFrom;
  const createdTo = range?.createdTo;
  return useQuery({
    queryKey: ['leads', tenantId, pipelineId, createdFrom ?? 'all', createdTo ?? 'all'],
    queryFn: async () => {
      if (!pipelineId) return [];
      const parts = [`tenant = "${tenantId}"`, `pipeline = "${pipelineId}"`];
      if (createdFrom) parts.push(`created >= "${createdFrom}"`);
      if (createdTo) parts.push(`created < "${createdTo}"`);
      const res = await pb.collection('leads').getList<Lead>(1, 200, {
        filter: parts.join(' && '),
        // Orden primario: `position` (drag-drop). Tiebreak por created
        // (lo más nuevo arriba entre mismos position). No usamos -updated:
        // el SLA y ediciones de valor reordenaban el kanban en silencio.
        sort: '+position,-created',
        expand: 'contact,stage,assigned_to',
      });
      return res.items;
    },
    enabled: !!tenantId && !!pipelineId,
  });
}

// Comparator que matchea el sort server-side: '+position,-created'.
// Usado en optimistic updates para re-ordenar la cache sin esperar refetch.
function leadKanbanSort(a: Lead, b: Lead): number {
  const ap = a.position ?? Number.MAX_SAFE_INTEGER;
  const bp = b.position ?? Number.MAX_SAFE_INTEGER;
  if (ap !== bp) return ap - bp;
  return (b.created ?? '').localeCompare(a.created ?? '');
}

/**
 * Mueve un lead a otro stage (drag entre columnas) Y/O reordena dentro
 * de la misma columna (drag dentro). Acepta `position` opcional para
 * inserción precisa: el caller la calcula como promedio entre vecinos.
 *
 * Si no se pasa `position`, el lead va al final (max+1000) — útil para
 * "drop sobre la columna entera, no sobre una card específica".
 *
 * Optimistic update + re-sort en cache reflejan el nuevo orden instante,
 * sin esperar refetch.
 */
export function useMoveLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      stageId,
      position,
      lost_reason,
    }: {
      leadId: string;
      stageId: string;
      position?: number;
      /** Set when entering/leaving a lost stage (clear with ''). */
      lost_reason?: string;
    }) => {
      const patch: Partial<Lead> = { stage: stageId };
      if (position !== undefined) patch.position = position;
      if (lost_reason !== undefined) patch.lost_reason = lost_reason;
      // requestKey:null — evita que un refetch de ['leads'] autocancelle el PATCH
      // a mitad de vuelo (el kanban invalida al settled; sin esto el move puede
      // parecer que "no pegó" y al refrescar vuelve al stage anterior).
      return pb.collection('leads').update<Lead>(leadId, patch, { requestKey: null });
    },
    onMutate: async ({ leadId, stageId, position, lost_reason }) => {
      const now = new Date().toISOString().replace('T', ' ').replace(/\..+/, '.000Z');
      const allKeys = qc.getQueryCache().findAll({ queryKey: ['leads'] });
      const snapshots: Array<{ key: readonly unknown[]; data: Lead[] | undefined }> = [];
      for (const q of allKeys) {
        const data = q.state.data as Lead[] | undefined;
        snapshots.push({ key: q.queryKey, data });
        if (data) {
          const next = data.map((l) =>
            l.id === leadId
              ? {
                  ...l,
                  stage: stageId,
                  ...(position !== undefined ? { position } : {}),
                  ...(lost_reason !== undefined ? { lost_reason } : {}),
                  updated: now,
                }
              : l,
          );
          next.sort(leadKanbanSort);
          qc.setQueryData<Lead[]>(q.queryKey, next);
        }
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const s of ctx.snapshots) {
          qc.setQueryData(s.key, s.data);
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
    },
  });
}

/**
 * Alias deprecado — retorna el mismo hook que useMoveLead. Mantenido para
 * que callers que solo cambian stage (sin position) sigan funcionando.
 * Preferir useMoveLead en código nuevo.
 */
export const useUpdateLeadStage = useMoveLead;

export function useContacts() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['contacts', tenantId],
    queryFn: async () => {
      const res = await pb.collection('contacts').getList<Contact>(1, 200, {
        filter: `tenant = "${tenantId}"`,
        sort: 'first_name',
      });
      return res.items;
    },
    enabled: !!tenantId,
  });
}

export function useLeadActivities(leadId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['activities', 'lead', tenantId, leadId],
    queryFn: async () => {
      if (!leadId) return [];
      const res = await pb.collection('activities').getList<Activity>(1, 100, {
        filter: `tenant = "${tenantId}" && lead = "${leadId}"`,
        sort: '-occurred_at',
        expand: 'actor',
      });
      return res.items;
    },
    enabled: !!tenantId && !!leadId,
    // Sin refetchInterval — el parpadeo cada 15s del loader global era peor
    // que ver el inbound tarde. Refetch on focus + invalidates tras send
    // cubren el flujo normal del operador.
  });
}

export function useLeadTasks(leadId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['tasks', 'lead', tenantId, leadId],
    queryFn: async () => {
      if (!leadId) return [];
      const res = await pb.collection('tasks').getList<Task>(1, 50, {
        filter: `tenant = "${tenantId}" && lead = "${leadId}"`,
        sort: 'status,due_date',
        expand: 'assignee',
      });
      return res.items;
    },
    enabled: !!tenantId && !!leadId,
  });
}

/** Tasks linked to a contact (with or without a lead). */
export function useContactTasks(contactId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['tasks', 'contact', tenantId, contactId],
    queryFn: async () => {
      if (!contactId) return [];
      const res = await pb.collection('tasks').getList<Task>(1, 50, {
        filter: `tenant = "${tenantId}" && contact = "${contactId}"`,
        sort: 'status,due_date',
        expand: 'assignee,lead',
      });
      return res.items;
    },
    enabled: !!tenantId && !!contactId,
  });
}

/** YYYY-MM-DD → PB date end-of-day UTC (same convention as existing UI). */
export function dueDateToPb(dateYmd: string | undefined): string | undefined {
  if (!dateYmd) return undefined;
  return `${dateYmd} 23:59:59.000Z`;
}

export function dueDateFromPb(iso: string | undefined): string {
  if (!iso) return '';
  // Avoid TZ off-by-one: take civil date from the stored string when possible.
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1]! : '';
}

/** Lee un lead por id con sus relaciones expandidas. Para la página /leads/$id. */
export function useLead(leadId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['lead', tenantId, leadId],
    queryFn: async () => {
      if (!leadId) return null;
      try {
        return await pb.collection('leads').getOne<Lead>(leadId, {
          expand: 'contact,stage,assigned_to,pipeline',
        });
      } catch {
        return null;
      }
    },
    enabled: !!tenantId && !!leadId,
  });
}

export function useLeadAttribution(leadId: string | undefined) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['attribution', 'lead', tenantId, leadId],
    queryFn: async () => {
      if (!leadId) return null;
      try {
        return await pb
          .collection('attribution')
          .getFirstListItem<Attribution>(
            `tenant = "${tenantId}" && lead = "${leadId}"`,
          );
      } catch {
        return null;
      }
    },
    enabled: !!tenantId && !!leadId,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// MUTATIONS — Lead, Task, Note, Owner
// ─────────────────────────────────────────────────────────────────────────

/**
 * Update parcial de un lead (title, value, currency, probability, source,
 * expected_close, lost_reason, assigned_to, custom_fields).
 */
export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      leadId,
      patch,
    }: {
      leadId: string;
      patch: Partial<Lead>;
    }) => {
      return pb.collection('leads').update<Lead>(leadId, patch, {
        expand: 'contact,stage,assigned_to',
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead'] });
      qc.invalidateQueries({ queryKey: ['leads-distinct-tags'] });
      qc.invalidateQueries({ queryKey: ['activities', 'lead'] });
      void vars;
    },
  });
}

/**
 * Crear lead manual desde el admin (Sprint S0.2).
 *
 * Replica el flow del endpoint público /api/leads pero desde el admin React,
 * para casos donde el cliente recibió el lead por teléfono o presencial:
 *
 *   1. find-or-create del contact (por email o phone, dentro del tenant)
 *   2. crea el lead en el primer stage open del pipeline elegido (default si no
 *      se indica)
 *   3. crea attribution con utm_source='manual'
 *   4. crea activity 'lead_created' con metadata.created_by='manual'
 *
 * NO dispara el workflow n8n (`01-lead-created`) — eso solo lo dispara el hook
 * del backend cuando entra por la API pública. Los leads manuales no necesitan
 * notificación email porque la persona que los creó ya estaba enterada.
 */
export interface CreateLeadManualInput {
  // contact
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  position?: string;
  /** Sitio del negocio. Se guarda en contact.custom_fields.website. */
  website?: string;
  /** Brief de calificación. Queda como primera nota del timeline. */
  notes?: string;
  // lead
  title: string;
  value?: number | null;
  currency?: 'USD' | 'CLP' | 'MXN' | 'COP' | 'ARS' | 'EUR' | null;
  source?: string;
  pipelineId?: string;
  assigned_to?: string | null;
}

export interface CreateLeadManualResult {
  lead: Lead;
  contact: Contact;
  /** true si se creó el contact en el flujo, false si se reusó uno existente */
  contactCreated: boolean;
}

export function useCreateLeadManual() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (
      input: CreateLeadManualInput,
    ): Promise<CreateLeadManualResult> => {
      if (!tenantId) throw new Error('No hay tenant activo seleccionado.');
      const email = input.email?.trim().toLowerCase() || '';
      // Normalizar a E.164 sin '+' antes de buscar/guardar — matchea con la
      // forma canónica que el sidecar WhatsApp guarda en contacts.phone.
      const phone = normalizePhone(input.phone) || '';
      if (!email && !phone) {
        throw new Error('Tienes que pasar al menos email o teléfono.');
      }

      // 1. find-or-create contact
      let contact: Contact | null = null;
      let contactCreated = false;

      // Buscar primero por email (más exacto), después por phone
      if (email) {
        try {
          contact = await pb
            .collection('contacts')
            .getFirstListItem<Contact>(
              `tenant = "${tenantId}" && email = "${email.replace(/"/g, '\\"')}"`,
              { requestKey: null },
            );
        } catch {
          // 404 → no existe
        }
      }
      if (!contact && phone) {
        try {
          contact = await pb
            .collection('contacts')
            .getFirstListItem<Contact>(
              `tenant = "${tenantId}" && phone = "${phone}"`,
              { requestKey: null },
            );
        } catch {
          // 404
        }
      }

      const website = input.website?.trim() || '';
      const customWithSite = (base: Record<string, unknown> | undefined) => {
        if (!website) return base;
        return { ...(base ?? {}), website };
      };

      if (!contact) {
        contact = await pb.collection('contacts').create<Contact>({
          tenant: tenantId,
          email: email || null,
          phone: phone || null,
          first_name: input.first_name?.trim() || '',
          last_name: input.last_name?.trim() || '',
          company: input.company?.trim() || '',
          position: input.position?.trim() || '',
          tags: ['manual'],
          ...(website ? { custom_fields: { website } } : {}),
        });
        contactCreated = true;
      } else {
        const patch: Record<string, unknown> = {};
        if (!contact.position && input.position?.trim()) patch.position = input.position.trim();
        if (!contact.company && input.company?.trim()) patch.company = input.company.trim();
        const prevFields =
          contact.custom_fields && typeof contact.custom_fields === 'object'
            ? contact.custom_fields
            : {};
        if (website && !prevFields.website) {
          patch.custom_fields = customWithSite(prevFields);
        }
        if (Object.keys(patch).length > 0) {
          contact = await pb.collection('contacts').update<Contact>(contact.id, patch);
        }
      }

      // 2. resolver pipeline + stage default
      let pipelineId = input.pipelineId;
      if (!pipelineId) {
        const pipelines = await pb
          .collection('pipelines')
          .getList<Pipeline>(1, 1, {
            filter: `tenant = "${tenantId}" && is_default = true && archived = false`,
            requestKey: null,
          });
        if (pipelines.items.length > 0) {
          pipelineId = pipelines.items[0]!.id;
        } else {
          // Fallback al primer pipeline activo
          const fallback = await pb
            .collection('pipelines')
            .getList<Pipeline>(1, 1, {
              filter: `tenant = "${tenantId}" && archived = false`,
              sort: 'created',
              requestKey: null,
            });
          if (fallback.items.length === 0) {
            throw new Error(
              'No hay pipelines configurados en este tenant. Crea uno en /pipelines.',
            );
          }
          pipelineId = fallback.items[0]!.id;
        }
      }
      const stages = await pb
        .collection('pipeline_stages')
        .getList<PipelineStage>(1, 1, {
          filter: `tenant = "${tenantId}" && pipeline = "${pipelineId}" && archived = false`,
          sort: 'position',
          requestKey: null,
        });
      if (stages.items.length === 0) {
        throw new Error('Este pipeline no tiene stages configurados.');
      }
      const stageId = stages.items[0]!.id;

      // 3. crear lead
      const lead = await pb.collection('leads').create<Lead>(
        {
          tenant: tenantId,
          contact: contact.id,
          pipeline: pipelineId,
          stage: stageId,
          title: input.title.trim(),
          value: input.value ?? null,
          currency: input.currency ?? null,
          source: input.source?.trim() || 'manual',
          assigned_to: input.assigned_to || null,
        },
        { expand: 'contact,stage,assigned_to' },
      );

      // 4. crear attribution con utm_source='manual'
      try {
        await pb.collection('attribution').create({
          tenant: tenantId,
          lead: lead.id,
          utm_source: 'manual',
        });
      } catch (err) {
        // Si attribution falla, no rompemos — el lead ya existe.
        console.warn('attribution create falló (no bloqueante):', err);
      }

      // 5. crear activity lead_created
      try {
        await pb.collection('activities').create({
          tenant: tenantId,
          contact: contact.id,
          lead: lead.id,
          type: 'note',
          direction: 'system',
          subject: 'Oportunidad creada a mano',
          body: input.notes?.trim() || 'Creada desde el admin.',
          metadata: {
            event: 'lead_created',
            created_by: 'manual',
            source: input.source?.trim() || 'manual',
          },
          occurred_at: new Date().toISOString(),
        });
      } catch (err) {
        console.warn('activity lead_created falló (no bloqueante):', err);
      }

      return { lead, contact, contactCreated };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

/** Crea una task (lead y/o contact opcionales). */
export function useCreateTask() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (input: {
      leadId?: string | null;
      contactId?: string | null;
      title: string;
      description?: string;
      type?: Task['type'];
      // assignee opcional — migration 1777200021.
      assignee?: string | null;
      due_date?: string;
      priority?: 'low' | 'normal' | 'high' | 'urgent';
    }) => {
      if (!tenantId) throw new Error('no active tenant');
      const payload: Record<string, unknown> = {
        tenant: tenantId,
        title: input.title.trim(),
        description: input.description ?? '',
        due_date: input.due_date || null,
        status: 'open',
        priority: input.priority ?? 'normal',
        type: input.type ?? 'todo',
      };
      if (input.leadId) payload.lead = input.leadId;
      if (input.contactId) payload.contact = input.contactId;
      if (input.assignee) payload.assignee = input.assignee;
      return pb.collection('tasks').create<Task>(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/** Marca una task como completada (o vuelve a open). Timeline note if lead. */
export function useToggleTaskDone() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async ({ task, done }: { task: Task; done: boolean }) => {
      const updated = await pb.collection('tasks').update<Task>(task.id, {
        status: done ? 'done' : 'open',
        completed_at: done ? new Date().toISOString() : null,
      });
      if (done && task.lead && tenantId) {
        try {
          await pb.collection('activities').create({
            tenant: tenantId,
            lead: task.lead,
            contact: task.contact || undefined,
            type: 'task',
            direction: 'system',
            subject: 'Tarea completada',
            body: task.title,
            actor: pb.authStore.record?.id,
            occurred_at: new Date().toISOString(),
            metadata: { event: 'task_completed', task_id: task.id },
          });
        } catch (err) {
          console.warn('[tasks] timeline activity on complete failed', err);
        }
      }
      return updated;
    },
    onSuccess: (_t, vars) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      if (vars.task.lead) {
        qc.invalidateQueries({ queryKey: ['activities'] });
      }
    },
  });
}

/** Update parcial de una task (title, due_date, priority, assignee, etc.). */
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      patch,
    }: {
      taskId: string;
      patch: Partial<Task>;
    }) => {
      return pb.collection('tasks').update<Task>(taskId, patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

/** Eliminar task. */
export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      await pb.collection('tasks').delete(taskId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

/** Crea una nota (activity.type=note) asociada al lead. */
export function useCreateNote() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      contactId?: string;
      subject?: string;
      body: string;
    }) => {
      if (!tenantId) throw new Error('no active tenant');
      return pb.collection('activities').create<Activity>({
        tenant: tenantId,
        lead: input.leadId,
        contact: input.contactId,
        type: 'note',
        direction: 'system',
        subject: input.subject ?? '',
        body: input.body,
        actor: pb.authStore.record?.id,
        occurred_at: new Date().toISOString(),
      });
    },
    onSuccess: (_a, vars) => {
      qc.invalidateQueries({
        queryKey: ['activities', 'lead', tenantId, vars.leadId],
      });
    },
  });
}

/** Lista usuarios del tenant activo (para selectors de assignee/owner). */
export function useTenantUsers() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['users', 'tenant', tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as User[];
      const res = await pb.collection('users').getList<User>(1, 100, {
        filter: `tenant = "${tenantId}"`,
        sort: 'name,email',
      });
      return res.items;
    },
    enabled: !!tenantId,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// MUTATIONS — Pipelines y Stages
// ─────────────────────────────────────────────────────────────────────────

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      is_default?: boolean;
    }) => {
      if (!tenantId) throw new Error('no active tenant');
      const slug = slugify(input.name);
      // Si is_default, desmarcar el actual default antes.
      if (input.is_default) {
        try {
          const current = await pb
            .collection('pipelines')
            .getFirstListItem<Pipeline>(`tenant="${tenantId}" && is_default=true`);
          await pb.collection('pipelines').update(current.id, { is_default: false });
        } catch {
          // sin default actual, ok
        }
      }
      return pb.collection('pipelines').create<Pipeline>({
        tenant: tenantId,
        name: input.name.trim(),
        slug,
        description: input.description ?? '',
        is_default: input.is_default ?? false,
        archived: false,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useUpdatePipeline() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (input: {
      pipelineId: string;
      patch: Partial<Pipeline> & { is_default?: boolean };
    }) => {
      if (!tenantId) throw new Error('no active tenant');
      // Si activamos default, desmarcar otros del tenant.
      if (input.patch.is_default === true) {
        const others = await pb.collection('pipelines').getFullList<Pipeline>({
          filter: `tenant="${tenantId}" && is_default=true && id != "${input.pipelineId}"`,
        });
        for (const o of others) {
          await pb.collection('pipelines').update(o.id, { is_default: false });
        }
      }
      return pb.collection('pipelines').update<Pipeline>(input.pipelineId, input.patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipelines'] }),
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string) => {
      // Borrar stages primero (PB cascade en relation NO siempre dispara desde JS).
      const stages = await pb.collection('pipeline_stages').getFullList<PipelineStage>({
        filter: `pipeline="${pipelineId}"`,
      });
      for (const s of stages) {
        await pb.collection('pipeline_stages').delete(s.id);
      }
      await pb.collection('pipelines').delete(pipelineId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipelines'] });
      qc.invalidateQueries({ queryKey: ['pipeline_stages'] });
    },
  });
}

export function useCreateStage() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: async (input: {
      pipelineId: string;
      name: string;
      stage_type?: 'open' | 'won' | 'lost';
      color?: string;
      position?: number;
    }) => {
      if (!tenantId) throw new Error('no active tenant');
      // Position: si no se da, próxima al final del pipeline.
      let position = input.position;
      if (position === undefined) {
        const last = await pb.collection('pipeline_stages').getFullList<PipelineStage>({
          filter: `pipeline="${input.pipelineId}"`,
          sort: '-position',
        });
        position = last[0]?.position != null ? (last[0].position as number) + 10 : 10;
      }
      return pb.collection('pipeline_stages').create<PipelineStage>({
        tenant: tenantId,
        pipeline: input.pipelineId,
        name: input.name.trim(),
        slug: slugify(input.name),
        position,
        color: input.color ?? '#6b7280',
        stage_type: input.stage_type ?? 'open',
        archived: false,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline_stages'] }),
  });
}

export function useUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      stageId: string;
      patch: Partial<PipelineStage>;
    }) => {
      return pb
        .collection('pipeline_stages')
        .update<PipelineStage>(input.stageId, input.patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline_stages'] }),
  });
}

export function useDeleteStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stageId: string) => {
      await pb.collection('pipeline_stages').delete(stageId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline_stages'] }),
  });
}

/**
 * Re-asigna `position` a cada stage según su orden en el array.
 * Usa pasos de 10 para dejar espacio para inserciones futuras sin reordenar todo.
 */
export function useReorderStages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedStageIds: string[]) => {
      let p = 10;
      for (const id of orderedStageIds) {
        await pb.collection('pipeline_stages').update(id, { position: p });
        p += 10;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pipeline_stages'] }),
  });
}

// ─────────────────────────────────────────────────────────────────────────
// LIST de leads con búsqueda + filtros + paginación (vista alternativa)
// ─────────────────────────────────────────────────────────────────────────

export interface LeadsListParams {
  pipelineId?: string;
  page?: number;
  perPage?: number;
  search?: string;
  stageId?: string;
  source?: string;
  assignedTo?: string;
  /** Tags a filtrar (multi-select OR). Vacío = sin filtro. */
  tags?: string[];
  /** Filtro por estado de la bandeja (nuevo/contactado/descartado). 'nuevo'
   *  incluye los registros con el campo vacío (leads previos a la feature). */
  inboxStatus?: 'nuevo' | 'contactado' | 'descartado';
  /** Filtro por fecha de creación (ISO datetime, inclusivo). */
  createdFrom?: string;
  /** Filtro por fecha de creación (ISO datetime, exclusivo). */
  createdTo?: string;
  /** Sort: campo prefijado con '-' para desc. Default '-created'. */
  sort?: string;
  /** Filtro por interacción con correo enviado (Resend). */
  emailEngagement?: '' | 'any' | 'opened' | 'clicked';
}

/** Construye el filtro PB compartido por la lista y el export a CSV. */
function buildLeadsFilter(tenantId: string, params: LeadsListParams): string {
  const {
    pipelineId,
    search = '',
    stageId,
    source,
    assignedTo,
    tags = [],
    inboxStatus,
    createdFrom,
    createdTo,
    emailEngagement,
  } = params;
  const filters: string[] = [`tenant = "${tenantId}"`];
  if (pipelineId) filters.push(`pipeline = "${pipelineId}"`);
  if (stageId) filters.push(`stage = "${stageId}"`);
  if (source) filters.push(`source = "${source}"`);
  if (assignedTo) filters.push(`assigned_to = "${assignedTo}"`);
  if (inboxStatus === 'nuevo') filters.push(`(inbox_status = "nuevo" || inbox_status = "")`);
  else if (inboxStatus) filters.push(`inbox_status = "${inboxStatus}"`);
  if (createdFrom) filters.push(`created >= "${createdFrom}"`);
  if (createdTo) filters.push(`created < "${createdTo}"`);
  if (emailEngagement === 'opened') filters.push(`email_engagement_kind = "opened"`);
  else if (emailEngagement === 'clicked') filters.push(`email_engagement_kind = "clicked"`);
  else if (emailEngagement === 'any') {
    filters.push(`(email_engagement_kind = "opened" || email_engagement_kind = "clicked")`);
  }
  if (tags.length > 0) {
    // tags se persiste como JSON-stringified array. Filtro OR con LIKE
    // contra el string serializado — funciona para tags sin caracteres
    // especiales. Escapamos comillas para evitar romper el filtro.
    const tagClauses = tags
      .map((t) => `tags ~ ${JSON.stringify('"' + t.replace(/"/g, '') + '"')}`)
      .join(' || ');
    filters.push(`(${tagClauses})`);
  }
  if (search) {
    const esc = search.replace(/"/g, '');
    filters.push(
      `(title ~ "${esc}" || contact.email ~ "${esc}" || contact.first_name ~ "${esc}" || contact.last_name ~ "${esc}" || contact.company ~ "${esc}")`,
    );
  }
  return filters.join(' && ');
}

export function useLeadsList(params: LeadsListParams) {
  const tenantId = useActiveTenantId();
  const {
    pipelineId,
    page = 1,
    perPage = 25,
    search = '',
    stageId,
    source,
    assignedTo,
    tags = [],
    inboxStatus,
    createdFrom,
    createdTo,
    sort = '-created',
    emailEngagement,
  } = params;
  return useQuery({
    queryKey: [
      'leads',
      'list',
      tenantId,
      pipelineId ?? 'all',
      page,
      perPage,
      search,
      stageId ?? 'all',
      source ?? 'all',
      assignedTo ?? 'all',
      tags.slice().sort().join(','),
      inboxStatus ?? 'all',
      createdFrom ?? 'all',
      createdTo ?? 'all',
      emailEngagement ?? 'all',
      sort,
    ],
    queryFn: async () => {
      const res = await pb.collection('leads').getList<Lead>(page, perPage, {
        filter: buildLeadsFilter(tenantId!, params),
        sort,
        expand: 'contact,stage,assigned_to',
      });
      return res;
    },
    enabled: !!tenantId,
  });
}

/**
 * Trae TODAS las oportunidades que matchean el filtro (para exportar a CSV).
 * No paginado — usa getFullList. No es un hook: se llama on-demand desde un
 * handler ("Descargar CSV"). El caller pasa el tenantId activo.
 */
export async function fetchLeadsForExport(
  tenantId: string,
  params: LeadsListParams,
): Promise<Lead[]> {
  return pb.collection('leads').getFullList<Lead>({
    filter: buildLeadsFilter(tenantId, params),
    sort: params.sort ?? '-created',
    expand: 'contact,stage',
    requestKey: null,
  });
}

/** Borra una oportunidad. Superadmin o client_admin del tenant (rules PB). */
export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId }: { leadId: string }) => {
      return pb.collection('leads').delete(leadId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Orígenes (source) distintos presentes en las oportunidades del tenant, con
 * su conteo. Para poblar el filtro de la bandeja. Client-side dedupe (PB no
 * hace DISTINCT); fields-limited, barato.
 */
export function useDistinctLeadSources() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['leads-distinct-sources', tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as { source: string; count: number }[];
      const items = await pb.collection('leads').getFullList<Lead>({
        filter: `tenant = "${tenantId}"`,
        fields: 'id,source',
        requestKey: null,
      });
      const counts = new Map<string, number>();
      for (const lead of items) {
        const s = (lead.source ?? '').trim();
        if (!s) continue;
        counts.set(s, (counts.get(s) ?? 0) + 1);
      }
      return Array.from(counts.entries())
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source));
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

// ═════════════════════════════════════════════════════════════════════════
// Tags helpers (leads.tags)
// ═════════════════════════════════════════════════════════════════════════
//
// `leads.tags` se persiste como columna JSON. El motor de Automatizaciones
// (`add_tag` step) escribe con `JSON.stringify(tags)`, que dependiendo de la
// versión de PB queda como string serializado dentro del JSON column. El SDK
// puede devolver array (escritura directa) O string (escritura del engine).
// `parseTags` normaliza ambos para que la UI siempre vea `string[]`.

export function parseTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw
      .filter((s) => typeof s === 'string')
      .map((s) => (s as string).trim())
      .filter((s) => s.length > 0);
  }
  if (typeof raw === 'string' && raw.trim()) {
    const txt = raw.trim();
    try {
      const parsed = JSON.parse(txt);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((s) => typeof s === 'string')
          .map((s) => (s as string).trim())
          .filter((s) => s.length > 0);
      }
    } catch {
      // Fallback: si no es JSON pero parece coma-separado, partir.
      if (txt.includes(',')) {
        return txt
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
      }
      return [txt];
    }
  }
  return [];
}

/**
 * Devuelve tags distintos del tenant (con count de leads), para popular
 * filtros multi-select. Hace un getFullList con `fields:'id,tags'` para
 * minimizar el payload — hasta ~5K leads/tenant esto está OK. Si pasa de
 * ahí, mover a `tenant_stats.tag_counts` mantenido por hook PB.
 */
export function useDistinctTagsByTenant() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: ['leads-distinct-tags', tenantId],
    queryFn: async () => {
      if (!tenantId) return [] as { tag: string; count: number }[];
      // Cualquier lead del tenant — el filtro `tags != null` no es confiable
      // contra JSON columns en PB; filtramos client-side los vacíos.
      const items = await pb.collection('leads').getFullList<Lead>({
        filter: `tenant = "${tenantId}"`,
        fields: 'id,tags',
        requestKey: null,
      });
      const counts = new Map<string, number>();
      for (const lead of items) {
        const tagsField = (lead as unknown as { tags?: unknown }).tags;
        for (const t of parseTags(tagsField)) {
          counts.set(t, (counts.get(t) ?? 0) + 1);
        }
      }
      return Array.from(counts.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
    },
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}
